import React, { useState, useEffect } from 'react';
import { FiCheckCircle, FiSearch, FiShare2, FiGlobe, FiTool, FiEye } from 'react-icons/fi';
import Swal from 'sweetalert2';

const API_URL = 'https://app.thundermaxhome.com/api.php';

export default function SEOManagerPage() {
  const [seo, setSeo] = useState({
    meta_title: '',
    meta_description: '',
    meta_keywords: '',
    og_title: '',
    og_description: '',
    og_image: '',
    google_verification: '',
    robots: 'index, follow',
    google_analytics_id: '',
    facebook_pixel_id: '',
    business_latitude: '',
    business_longitude: '',
    business_hours: '',
    faq_json: '[]'
  });
  const [faqList, setFaqList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    fetchSEO();
  }, []);

  const fetchSEO = async () => {
    setIsLoading(true);
    try {
      const res = await fetch(`${API_URL}?action=get_seo_settings`);
      const result = await res.json();
      if (result.success && result.data) {
        setSeo(result.data);
        try {
          const parsed = JSON.parse(result.data.faq_json || '[]');
          setFaqList(Array.isArray(parsed) ? parsed : []);
        } catch (e) {
          setFaqList([]);
        }
      }
    } catch (error) {
      console.error('Error fetching SEO:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setSeo(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    Swal.fire({
      title: 'กำลังบันทึกตั้งค่า SEO...',
      allowOutsideClick: false,
      didOpen: () => Swal.showLoading()
    });

    const payload = {
      ...seo,
      faq_json: JSON.stringify(faqList)
    };

    try {
      const res = await fetch(`${API_URL}?action=update_seo_settings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const result = await res.json();
      if (result.success) {
        Swal.fire('สำเร็จ!', 'อัปเดตการตั้งค่า SEO บนเซิร์ฟเวอร์หลักเรียบร้อยแล้ว', 'success');
      } else {
        Swal.fire('ข้อผิดพลาด', result.message || 'ไม่สามารถบันทึกได้', 'error');
      }
    } catch (error) {
      Swal.fire('ข้อผิดพลาด', 'ไม่สามารถเชื่อมต่อเซิร์ฟเวอร์ได้', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-6 pb-20 animate-fade-in text-left text-gray-800 dark:text-gray-100">
      
      {/* Header */}
      <div>
        <h1 className="text-2xl font-black text-gray-900 dark:text-white flex items-center gap-2">
          🛠️ ระบบจัดการ SEO & Metadata (SEO Engine Manager)
        </h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          ปรับแต่งหัวข้อ คำอธิบาย และคีย์เวิร์ดของเว็บไซต์เพื่อให้ติดหน้าแรกของ Google และแชร์ลง LINE/Facebook ให้ขึ้นหน้าปกสวยงาม
        </p>
      </div>

      {isLoading ? (
        <div className="text-center py-20 text-blue-500 font-bold">กำลังโหลดการตั้งค่า SEO จากเซิร์ฟเวอร์...</div>
      ) : (
        <form onSubmit={handleSubmit} className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          
          {/* คอลัมน์ตั้งค่า (ฟอร์ม) */}
          <div className="lg:col-span-2 space-y-6">
            
            {/* 1. Global Settings */}
            <div className="bg-white dark:bg-[#131825] p-6 rounded-2xl border border-gray-200 dark:border-[#1e2330] shadow-sm space-y-4">
              <h3 className="text-sm font-black text-gray-800 dark:text-white flex items-center gap-1.5 pb-2.5 border-b border-gray-100 dark:border-gray-800">
                <FiGlobe className="text-blue-500" /> ตั้งค่า SEO ทั่วไป (Google Search)
              </h3>
              
              <div className="space-y-4">
                <div className="space-y-1.5">
                  <label className="block text-[11px] font-bold text-gray-500 dark:text-gray-400 uppercase">หัวข้อชื่อเว็บหลัก (Meta Title)</label>
                  <input 
                    type="text" 
                    name="meta_title"
                    value={seo.meta_title}
                    onChange={handleChange}
                    required
                    maxLength={70}
                    placeholder="เช่น Thunder Maxhome - บริการล้างแอร์ ซ่อมแอร์ ติดตั้งแอร์บ้าน"
                    className="w-full p-2.5 bg-gray-50 dark:bg-gray-800 border border-gray-250 dark:border-gray-700 text-gray-900 dark:text-white rounded-xl text-xs outline-none"
                  />
                  <p className="text-[10px] text-gray-450 dark:text-gray-500 leading-normal">
                    หัวเรื่องที่แสดงบนหน้าค้นหา Google แนะนำความยาวระหว่าง 50-60 ตัวอักษร (จำกัดสูงสุด 70)
                  </p>
                </div>

                <div className="space-y-1.5">
                  <label className="block text-[11px] font-bold text-gray-500 dark:text-gray-400 uppercase">คำอธิบายรายละเอียดเว็บ (Meta Description)</label>
                  <textarea 
                    name="meta_description"
                    value={seo.meta_description}
                    onChange={handleChange}
                    required
                    rows={3}
                    maxLength={160}
                    placeholder="เช่น บริการล้างแอร์ ซ่อมแอร์ ย้ายแอร์ ติดตั้งเครื่องปรับอากาศคุณภาพมาตรฐานระดับสูงด้วยทีมช่างวิชาชีพที่เชี่ยวชาญ คุ้มค่าและรวดเร็ว..."
                    className="w-full p-2.5 bg-gray-50 dark:bg-gray-800 border border-gray-250 dark:border-gray-700 text-gray-900 dark:text-white rounded-xl text-xs outline-none resize-none"
                  />
                  <p className="text-[10px] text-gray-450 dark:text-gray-500 leading-normal">
                    ข้อมูลย่อสรุปรายละเอียดที่แสดงในผลการค้นหา Google แนะนำความยาวประมาณ 120-150 ตัวอักษร (จำกัดสูงสุด 160)
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <label className="block text-[11px] font-bold text-gray-500 dark:text-gray-400 uppercase">คีย์เวิร์ดของเว็บ (Meta Keywords)</label>
                    <input 
                      type="text" 
                      name="meta_keywords"
                      value={seo.meta_keywords}
                      onChange={handleChange}
                      placeholder="เช่น ล้างแอร์, ซ่อมแอร์, ย้ายแอร์, ติดตั้งแอร์"
                      className="w-full p-2.5 bg-gray-50 dark:bg-gray-800 border border-gray-250 dark:border-gray-700 text-gray-900 dark:text-white rounded-xl text-xs outline-none"
                    />
                    <p className="text-[10px] text-gray-450 dark:text-gray-500 leading-normal">
                      คำค้นหาเป้าหมาย คั่นด้วยเครื่องหมายจุลภาค (Comma)
                    </p>
                  </div>

                  <div className="space-y-1.5">
                    <label className="block text-[11px] font-bold text-gray-500 dark:text-gray-400 uppercase">สิทธิ์การเก็บข้อมูลการค้นหา (Robots Settings)</label>
                    <select
                      name="robots"
                      value={seo.robots}
                      onChange={handleChange}
                      className="w-full p-2.5 bg-gray-50 dark:bg-gray-800 border border-gray-250 dark:border-gray-700 text-gray-900 dark:text-white rounded-xl text-xs outline-none font-bold"
                    >
                      <option value="index, follow">index, follow (อนุญาตให้แสดงบน Google เสมอ)</option>
                      <option value="noindex, nofollow">noindex, nofollow (ซ่อนหน้านี้ไม่ให้คนค้นเจอใน Google)</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {/* 2. Open Graph (Social Sharing) */}
            <div className="bg-white dark:bg-[#131825] p-6 rounded-2xl border border-gray-200 dark:border-[#1e2330] shadow-sm space-y-4">
              <h3 className="text-sm font-black text-gray-800 dark:text-white flex items-center gap-1.5 pb-2.5 border-b border-gray-100 dark:border-gray-800">
                <FiShare2 className="text-indigo-500" /> ข้อมูลส่งลิงก์แชร์ไลน์/เฟซบุ๊ก (Open Graph)
              </h3>

              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <label className="block text-[11px] font-bold text-gray-500 dark:text-gray-400 uppercase">หัวข้อเวลาแชร์ (OG Title)</label>
                    <input 
                      type="text" 
                      name="og_title"
                      value={seo.og_title}
                      onChange={handleChange}
                      placeholder="เช่น คิวจองล้างแอร์ ซ่อมแอร์ด่วน - Thunder Maxhome"
                      className="w-full p-2.5 bg-gray-50 dark:bg-gray-800 border border-gray-250 dark:border-gray-700 text-gray-900 dark:text-white rounded-xl text-xs outline-none"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="block text-[11px] font-bold text-gray-500 dark:text-gray-400 uppercase">ลิงก์ภาพหน้าปกจดหมาย (OG Image URL)</label>
                    <input 
                      type="url" 
                      name="og_image"
                      value={seo.og_image}
                      onChange={handleChange}
                      placeholder="เช่น https://www.thundermaxhome.com/logo.png"
                      className="w-full p-2.5 bg-gray-50 dark:bg-gray-800 border border-gray-250 dark:border-gray-700 text-gray-900 dark:text-white rounded-xl text-xs outline-none font-mono"
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <label className="block text-[11px] font-bold text-gray-500 dark:text-gray-400 uppercase">คำอธิบายเวลาแชร์ (OG Description)</label>
                  <textarea 
                    name="og_description"
                    value={seo.og_description}
                    onChange={handleChange}
                    rows={2}
                    placeholder="เช่น จองคิวช่างได้ทันใจ บริการเครื่องปรับอากาศดีเยี่ยมจากมืออาชีพ"
                    className="w-full p-2.5 bg-gray-50 dark:bg-gray-800 border border-gray-250 dark:border-gray-700 text-gray-900 dark:text-white rounded-xl text-xs outline-none resize-none"
                  />
                </div>
              </div>
            </div>

            {/* 3. Google Verification */}
            <div className="bg-white dark:bg-[#131825] p-6 rounded-2xl border border-gray-200 dark:border-[#1e2330] shadow-sm space-y-4">
              <h3 className="text-sm font-black text-gray-800 dark:text-white flex items-center gap-1.5 pb-2.5 border-b border-gray-100 dark:border-gray-800">
                <FiTool className="text-red-500" /> ยืนยันสิทธิ์ Google Search Console
              </h3>
              
              <div className="space-y-1.5">
                <label className="block text-[11px] font-bold text-gray-500 dark:text-gray-400 uppercase">โค้ด Google Site Verification (Content ID)</label>
                <input 
                  type="text" 
                  name="google_verification"
                  value={seo.google_verification}
                  onChange={handleChange}
                  placeholder="เช่น google-site-verification=abc123xyz..."
                  className="w-full p-2.5 bg-gray-50 dark:bg-gray-800 border border-gray-250 dark:border-gray-700 text-gray-900 dark:text-white rounded-xl text-xs outline-none font-mono"
                />
                <p className="text-[10px] text-gray-450 dark:text-gray-500 leading-normal">
                  นำโค้ดในแท็ก content มาใส่เพื่อแสดงความเป็นเจ้าของเว็บกับ Google Search Console
                </p>
              </div>
            </div>

            {/* 4. Analytics & Tracking Pixels */}
            <div className="bg-white dark:bg-[#131825] p-6 rounded-2xl border border-gray-200 dark:border-[#1e2330] shadow-sm space-y-4">
              <h3 className="text-sm font-black text-gray-800 dark:text-white flex items-center gap-1.5 pb-2.5 border-b border-gray-100 dark:border-gray-800">
                <span className="text-emerald-500">📊</span> รหัสวิเคราะห์และติดตามข้อมูล (Analytics & Tracking Codes)
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="block text-[11px] font-bold text-gray-500 dark:text-gray-400 uppercase">Google Analytics (GA4) ID</label>
                  <input 
                    type="text" 
                    name="google_analytics_id"
                    value={seo.google_analytics_id || ''}
                    onChange={handleChange}
                    placeholder="เช่น G-XXXXXXXXXX"
                    className="w-full p-2.5 bg-gray-50 dark:bg-gray-800 border border-gray-250 dark:border-gray-700 text-gray-900 dark:text-white rounded-xl text-xs outline-none font-mono"
                  />
                  <p className="text-[10px] text-gray-450 dark:text-gray-500 leading-normal">
                    รหัสวัดผลการเข้าเยี่ยมชมเว็บไซต์ของ Google Analytics
                  </p>
                </div>
                <div className="space-y-1.5">
                  <label className="block text-[11px] font-bold text-gray-500 dark:text-gray-400 uppercase">Facebook Pixel ID</label>
                  <input 
                    type="text" 
                    name="facebook_pixel_id"
                    value={seo.facebook_pixel_id || ''}
                    onChange={handleChange}
                    placeholder="เช่น 123456789012345"
                    className="w-full p-2.5 bg-gray-50 dark:bg-gray-800 border border-gray-250 dark:border-gray-700 text-gray-900 dark:text-white rounded-xl text-xs outline-none font-mono"
                  />
                  <p className="text-[10px] text-gray-450 dark:text-gray-500 leading-normal">
                    รหัสติดตามผู้เข้าชมสำหรับการโฆษณาบน Facebook / Meta
                  </p>
                </div>
              </div>
            </div>

            {/* 5. Local SEO Coordinates & Hours */}
            <div className="bg-white dark:bg-[#131825] p-6 rounded-2xl border border-gray-200 dark:border-[#1e2330] shadow-sm space-y-4">
              <h3 className="text-sm font-black text-gray-800 dark:text-white flex items-center gap-1.5 pb-2.5 border-b border-gray-100 dark:border-gray-800">
                <span className="text-orange-500">📍</span> พิกัดและเวลาทำการธุรกิจสำหรับ Local SEO Schema
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-1.5">
                  <label className="block text-[11px] font-bold text-gray-500 dark:text-gray-400 uppercase">ละติจูด (Latitude)</label>
                  <input 
                    type="text" 
                    name="business_latitude"
                    value={seo.business_latitude || ''}
                    onChange={handleChange}
                    placeholder="เช่น 13.6844"
                    className="w-full p-2.5 bg-gray-50 dark:bg-gray-800 border border-gray-250 dark:border-gray-700 text-gray-900 dark:text-white rounded-xl text-xs outline-none font-mono"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="block text-[11px] font-bold text-gray-500 dark:text-gray-400 uppercase">ลองจิจูด (Longitude)</label>
                  <input 
                    type="text" 
                    name="business_longitude"
                    value={seo.business_longitude || ''}
                    onChange={handleChange}
                    placeholder="เช่น 100.6156"
                    className="w-full p-2.5 bg-gray-50 dark:bg-gray-800 border border-gray-250 dark:border-gray-700 text-gray-900 dark:text-white rounded-xl text-xs outline-none font-mono"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="block text-[11px] font-bold text-gray-500 dark:text-gray-400 uppercase">เวลาทำการ (Business Hours)</label>
                  <input 
                    type="text" 
                    name="business_hours"
                    value={seo.business_hours || ''}
                    onChange={handleChange}
                    placeholder="เช่น Mo-Su 08:00-18:00"
                    className="w-full p-2.5 bg-gray-50 dark:bg-gray-800 border border-gray-250 dark:border-gray-700 text-gray-900 dark:text-white rounded-xl text-xs outline-none"
                  />
                </div>
              </div>
              <p className="text-[10px] text-gray-450 dark:text-gray-550 leading-normal font-bold">
                ระบุพิกัดจีพีเอสและเวลาทำการของบริษัทเพื่อให้ Google บอทใช้ทำดัชนีร้านค้าท้องถิ่น (Local Business Profile)
              </p>
            </div>

            {/* 6. FAQ Snippets Manager */}
            <div className="bg-white dark:bg-[#131825] p-6 rounded-2xl border border-gray-200 dark:border-[#1e2330] shadow-sm space-y-4">
              <div className="flex justify-between items-center pb-2.5 border-b border-gray-100 dark:border-gray-800">
                <h3 className="text-sm font-black text-gray-800 dark:text-white flex items-center gap-1.5">
                  <span className="text-amber-500">❓</span> คำถามที่พบบ่อย (FAQ Snippets - Rich Results)
                </h3>
                <button
                  type="button"
                  onClick={() => setFaqList(prev => [...prev, { question: '', answer: '' }])}
                  className="px-3 py-1.5 bg-blue-50 dark:bg-blue-950/40 text-blue-600 dark:text-blue-400 text-[10px] font-black rounded-lg hover:bg-blue-100 transition"
                >
                  + เพิ่มข้อคำถาม
                </button>
              </div>

              {faqList.length === 0 ? (
                <div className="text-center py-6 text-[11px] text-gray-400">ยังไม่มีรายการคำถามที่พบบ่อย (ข้อมูลคำถามจะช่วยให้เว็บขึ้นกรอบคำตอบเด่นในหน้า Google)</div>
              ) : (
                <div className="space-y-4 max-h-[300px] overflow-y-auto pr-1">
                  {faqList.map((faq, index) => (
                    <div key={index} className="p-3.5 bg-gray-50 dark:bg-gray-900 border border-gray-150 dark:border-gray-800 rounded-xl space-y-3 relative">
                      <button
                        type="button"
                        onClick={() => setFaqList(prev => prev.filter((_, i) => i !== index))}
                        className="absolute top-2 right-2 text-gray-400 hover:text-red-500 text-xs font-bold"
                        title="ลบข้อนี้"
                      >
                        &times; ลบ
                      </button>
                      <div className="space-y-1">
                        <label className="block text-[10px] font-bold text-gray-400 uppercase">คำถามข้อที่ {index + 1}</label>
                        <input
                          type="text"
                          value={faq.question}
                          onChange={(e) => {
                            const newFaq = [...faqList];
                            newFaq[index].question = e.target.value;
                            setFaqList(newFaq);
                          }}
                          required
                          placeholder="เช่น ล้างแอร์ใช้เวลานานเท่าไหร่?"
                          className="w-[85%] p-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white rounded-lg text-xs outline-none"
                        />
                      </div>
                      <div className="space-y-1">
                        <label className="block text-[10px] font-bold text-gray-400 uppercase">คำตอบข้อที่ {index + 1}</label>
                        <textarea
                          value={faq.answer}
                          onChange={(e) => {
                            const newFaq = [...faqList];
                            newFaq[index].answer = e.target.value;
                            setFaqList(newFaq);
                          }}
                          required
                          rows={2}
                          placeholder="เช่น โดยปกติใช้เวลาประมาณ 45-60 นาทีต่อเครื่อง ขึ้นอยู่กับสภาพความสกปรกและประเภทแอร์ครับ"
                          className="w-full p-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white rounded-lg text-xs outline-none resize-none"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* ปุ่มกดส่งฟอร์ม */}
            <div className="flex justify-end">
              <button
                type="submit"
                disabled={isSaving}
                className="px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-black text-xs rounded-xl flex items-center gap-1.5 shadow-md transition"
              >
                <FiCheckCircle size={15} /> {isSaving ? 'กำลังบันทึกข้อมูล...' : 'บันทึกการตั้งค่า SEO ของบริษัท'}
              </button>
            </div>

          </div>

          {/* คอลัมน์พรีวิว (ตัวอย่างรูปโฆษณา) */}
          <div className="lg:col-span-1 space-y-6">
            
            {/* Google Result Preview */}
            <div className="bg-white dark:bg-[#131825] p-5 rounded-2xl border border-gray-200 dark:border-[#1e2330] shadow-sm space-y-3">
              <h3 className="text-xs font-bold text-gray-850 dark:text-white flex items-center gap-1">
                <FiEye className="text-emerald-500" /> ตัวอย่างบน Google Search (Google Preview)
              </h3>
              
              <div className="bg-gray-50 dark:bg-[#0b0f19] p-4 rounded-xl border border-gray-150 dark:border-gray-800 space-y-1.5 font-sans leading-relaxed text-xs">
                <p className="text-[10px] text-gray-400 dark:text-gray-500 truncate">https://www.thundermaxhome.com</p>
                <h4 className="text-[#1a0dab] dark:text-[#8ab4f8] hover:underline font-medium text-sm leading-normal cursor-pointer break-words">
                  {seo.meta_title || 'Thunder Maxhome - ผู้เชี่ยวชาญระบบเครื่องปรับอากาศ'}
                </h4>
                <p className="text-[#4d5156] dark:text-[#bdc1c6] text-[11px] leading-relaxed break-words line-clamp-3">
                  {seo.meta_description || 'รายละเอียดรายละเอียดของบริษัทที่จะขึ้นแสดงผลบนหน้าค้นหาการล้างแอร์ ซ่อมแอร์ ย้ายและติดตั้งครบครัน...'}
                </p>
              </div>
            </div>

            {/* Line/Facebook Share Preview */}
            <div className="bg-white dark:bg-[#131825] p-5 rounded-2xl border border-gray-200 dark:border-[#1e2330] shadow-sm space-y-3">
              <h3 className="text-xs font-bold text-gray-855 dark:text-white flex items-center gap-1">
                <FiShare2 className="text-indigo-500" /> ตัวอย่างบน LINE / Social Media Share
              </h3>
              
              <div className="bg-gray-100 dark:bg-[#0f172a] rounded-xl overflow-hidden border border-gray-200 dark:border-gray-800 flex flex-col">
                <div className="aspect-[1.91/1] w-full bg-gray-200 dark:bg-[#1e293b] flex items-center justify-center overflow-hidden">
                  {seo.og_image ? (
                    <img 
                      src={seo.og_image} 
                      alt="OG Cover Image"
                      className="w-full h-full object-cover"
                      onError={(e) => { e.target.src = 'https://www.thundermaxhome.com/logo.png'; }}
                    />
                  ) : (
                    <span className="text-[10px] text-gray-400">ไม่มีรูปภาพหน้าปก (OG Image)</span>
                  )}
                </div>
                <div className="p-3 bg-white dark:bg-[#131825] border-t border-gray-150 dark:border-gray-800 text-[11px] space-y-0.5 leading-normal">
                  <h4 className="font-black text-gray-800 dark:text-white truncate">
                    {seo.og_title || seo.meta_title || 'Thunder Maxhome'}
                  </h4>
                  <p className="text-gray-500 dark:text-gray-400 line-clamp-2">
                    {seo.og_description || seo.meta_description || 'รายละเอียดแชร์ลงสื่อสังคมออนไลน์...'}
                  </p>
                  <p className="text-[9px] text-gray-400/80 dark:text-gray-550 font-mono truncate pt-1">
                    www.thundermaxhome.com
                  </p>
                </div>
              </div>
            </div>

          </div>

        </form>
      )}

    </div>
  );
}
